package com.siriusxm.pia.operations.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.charts.Chart
import com.siriusxm.cmc.charts.ChartDetails
import com.siriusxm.cmc.charts.ChartType
import com.siriusxm.cmc.charts.hex
import com.siriusxm.pia.components.*
import com.siriusxm.unifiedcontent.cmc.CMCService
import com.siriusxm.unifiedcontent.cmc.MetricQuery
import com.siriusxm.unifiedcontent.cmc.MetricSeries
import com.siriusxm.unifiedcontent.cmc.MetricsQueryRequest
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Colors used for chart datasets, cycling when more series than colors are returned.
 * Ordered to maximize contrast between adjacent series.
 */
private val CHART_COLORS = listOf(
    "#36A2EB", // blue
    "#FF6384", // red/pink
    "#4BC0C0", // teal
    "#FF9F40", // orange
    "#9966FF", // purple
    "#FFCD56", // yellow
    "#2ECC71", // green
    "#C9CBCF", // gray
)

/**
 * Configuration for the [MetricsChart] composable.
 *
 * @property client The CMCService client used to query metrics from the backend.
 * @property initialTimeRange The time range shown on first render. Defaults to the last 1 hour.
 * @property title Optional title displayed above the chart.
 * @property yAxisLabel Optional label for the Y axis (e.g., "Seconds", "Requests").
 * @property showFilters Whether to show the time range selector and DataDog link controls.
 * @property valueTransform Optional function applied to each raw metric value before plotting.
 *   Use this to convert units (e.g. `{ it / 1000.0 }` to convert milliseconds → seconds).
 */
class MetricsChartConfig {
    var client: CMCService? = null
    var initialTimeRange: TimeRange = TimeRange(PeriodType.Hours.periodOf(1))
    var title: String? = null
    var yAxisLabel: String? = null
    var showFilters: Boolean = true
    var valueTransform: ((Double) -> Double)? = null

    internal val queries = mutableListOf<MetricQuery>()
    internal var onDatadogUrlReceived: ((String) -> Unit)? = null

    /**
     * Callback invoked when a DataDog Metrics Explorer URL is received from the API response.
     * Use this to surface the URL outside the chart (e.g. in a Box menu action).
     */
    fun onDatadogUrl(callback: (String) -> Unit) {
        onDatadogUrlReceived = callback
    }

    /**
     * Add a named DataDog metrics query to the chart.
     *
     * The [name] is used as the dataset label (combined with the series scope for
     * grouped queries).  The [ddQuery] is a standard DataDog metrics query string,
     * e.g. `"p99:my.service.latency{env:prod} by {entity_type}"`.
     *
     * Multiple calls to [query] result in multiple query lines plotted on the same chart.
     */
    fun query(name: String, ddQuery: String) {
        queries += MetricQuery(name = name, query = ddQuery)
    }
}

/**
 * A composable that queries one or more DataDog metric timeseries via the CMC API and
 * renders the results as a line chart.
 *
 * Features:
 * - Interactive time range selector (1h, 3h, 6h, 12h, 1d, 1w)
 * - Multiple named queries plotted as separate datasets on a single chart
 * - Automatic multi-series support for "by {tag}" grouping in queries
 * - "View in DataDog" link to the Metrics Explorer
 * - Loading and error states
 *
 * Usage:
 * ```kotlin
 * MetricsChart {
 *     client = cmcService
 *     title = "P99 Publish Delay"
 *     yAxisLabel = "Seconds"
 *     query("p99", "p99:app_uni_content_catalog.entity.publish.delay{env:prod} by {entity_type}")
 * }
 * ```
 *
 * @param config Configuration block applied to a [MetricsChartConfig] instance.
 */
@Composable
fun MetricsChart(config: MetricsChartConfig.() -> Unit) {
    val viewerConfig = MetricsChartConfig().apply(config)
    val client = viewerConfig.client ?: return

    var timeRange by remember { mutableStateOf(viewerConfig.initialTimeRange) }
    var series by remember { mutableStateOf<List<MetricSeries>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Fetch metrics whenever the time range changes. The LaunchedEffect key ensures a
    // new coroutine is launched (and the previous one cancelled) on each time range change.
    LaunchedEffect(timeRange) {
        if (viewerConfig.queries.isEmpty()) return@LaunchedEffect

        isLoading = true
        error = null

        try {
            val range = timeRange.range(Clock.System.now())
            val response = client.operations.queryMetrics(
                MetricsQueryRequest(
                    queries = viewerConfig.queries,
                    from = range.start,
                    to = range.endInclusive
                )
            )
            series = response.series.orEmpty()
            response.url?.let { viewerConfig.onDatadogUrlReceived?.invoke(it) }
        } catch (e: Exception) {
            error = e.message ?: "Failed to load metrics"
        } finally {
            isLoading = false
        }
    }

    Style(OperationsStyles)

    Column(1.cssRem) {
        // Controls row: time range selector, right-aligned
        if (viewerConfig.showFilters) {
            Div({
                classes(OperationsStyles.logsViewerControls)
                style { justifyContent(JustifyContent.FlexEnd) }
            }) {
                Div({ classes(OperationsStyles.logsViewerFilters) }) {
                    timeRangeSelect(selected = timeRange) { newRange ->
                        if (newRange != null) timeRange = newRange
                    }
                }
            }
        }

        // Error state
        error?.let { errorMessage ->
            Div({
                style {
                    padding(10.px)
                    backgroundColor(Color("#ffebee"))
                    color(Color("#c62828"))
                    borderRadius(4.px)
                }
            }) {
                Text(errorMessage)
            }
        }

        // Loading state (only shown before first data arrives)
        if (isLoading && series.isEmpty()) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("Loading metrics...")
            }
        }

        // Empty state (loaded but no data)
        if (!isLoading && error == null && series.isEmpty()) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("No metrics data found for the selected time range")
            }
        }

        // Chart: rendered once we have at least one series with data points
        if (series.isNotEmpty()) {
            val multipleQueries = viewerConfig.queries.size > 1
            val chartDetails = buildChartDetails(
                series,
                multipleQueries,
                viewerConfig.yAxisLabel,
                viewerConfig.valueTransform
            )
            Chart(chartDetails, key = timeRange)
        }
    }
}

/**
 * Builds the [ChartDetails] for a list of [MetricSeries] returned from DataDog.
 *
 * Uses a CATEGORY scale (no date adapter required).  All unique timestamps across every
 * series are unioned, sorted, and formatted as "HH:MM" strings for the x-axis labels.
 * Each series is then aligned to those labels as a parallel numeric array; missing values
 * at a given timestamp are represented as [Double.NaN] so Chart.js renders them as gaps.
 *
 * Each [MetricSeries] becomes one dataset (one line) on the chart.  Colors cycle through
 * [CHART_COLORS].  The dataset label uses the series scope when there is only one query
 * (e.g., "entity_type:show"), or combines the query name and scope for multiple queries
 * (e.g., "p99: entity_type:show").
 *
 * @param series All series returned from the metrics query.
 * @param multipleQueries Whether the chart was configured with more than one query.
 * @param yAxisLabel Optional Y axis label.
 */
private fun buildChartDetails(
    series: List<MetricSeries>,
    multipleQueries: Boolean,
    yAxisLabel: String?,
    valueTransform: ((Double) -> Double)?
): ChartDetails {
    // Union all timestamps across every series, deduplicated and sorted ascending.
    val allTimestamps = series
        .flatMap { s -> s.points.orEmpty().map { it.timestamp } }
        .distinct()
        .sorted()

    return ChartDetails {
        type = ChartType.LINE

        data {
            // X-axis labels: one per unique timestamp, formatted as "HH:MM".
            labels = allTimestamps.map { formatTimestampLabel(it) }

            series.forEachIndexed { index, s ->
                val color = CHART_COLORS[index % CHART_COLORS.size]
                // Map timestamp → value for O(1) lookup when building the data array.
                val tsToValue: Map<Long, Double?> = s.points.orEmpty()
                    .associate { it.timestamp to it.value }

                dataset {
                    label = seriesLabel(s, multipleQueries)
                    borderColor = hex(color)
                    backgroundColor = hex(color)
                    tension = 0.1
                    pointRadius = 0
                    pointHoverRadius = 4
                    borderWidth = 2
                    // Parallel array aligned to allTimestamps; NaN renders as a gap.
                    data = allTimestamps.map { ts ->
                        val raw = tsToValue.getOrElse(ts) { Double.NaN } ?: Double.NaN
                        val transformed = if (raw.isNaN() || valueTransform == null) raw
                                          else valueTransform(raw)
                        transformed as Any
                    }
                }
            }
        }

        options {
            maintainAspectRatio = true
            aspectRatio = 2.5

            if (yAxisLabel != null) {
                scales {
                    y {
                        title {
                            display = true
                            text = yAxisLabel
                        }
                    }
                }
            }

            plugins {
                legend {
                    // Show legend when there is more than one series so users can
                    // distinguish lines; hide it for single-series charts to save space.
                    display = series.size > 1
                }
            }
        }
    }
}

/**
 * Formats an epoch-millisecond timestamp as an "HH:MM" string using the ISO-8601
 * representation produced by [Instant.toString] (always UTC).
 *
 * Example: 1745280600000 → "14:30"
 */
private fun formatTimestampLabel(ms: Long): String =
    Instant.fromEpochMilliseconds(ms)
        .toString()          // "2026-04-21T14:30:00.000Z"
        .substringAfter("T") // "14:30:00.000Z"
        .substring(0, 5)     // "14:30"

/**
 * Derives a human-readable label for a single [MetricSeries] in the chart legend.
 *
 * - Single-query charts: uses the scope (e.g., "entity_type:show"), falling back to
 *   the display name if scope is blank, then to the query name.
 * - Multi-query charts: prefixes the label with the query name to disambiguate
 *   (e.g., "p99: entity_type:show").
 */
private fun seriesLabel(series: MetricSeries, multipleQueries: Boolean): String {
    val scope = series.scope?.takeIf { it.isNotBlank() }
    val displayName = series.displayName?.takeIf { it.isNotBlank() }
    val queryName = series.queryName ?: ""

    val baseLabel = scope ?: displayName ?: queryName

    return if (multipleQueries && queryName.isNotBlank() && baseLabel != queryName) {
        "$queryName: $baseLabel"
    } else {
        baseLabel
    }
}
